import { fetchSpartacusVenueUsers } from 'src/utils/spartacusApi'
import { mapSpartacusVenueUsersResponse } from 'src/utils/spartacusMappers'
import { spartacusUserToCatchBoardBuyer } from 'src/utils/catchBoardBuyerHelpers'

/**
 * Load venue users for linked Catch Board venues (Spartacus is source of truth).
 * Merges users across venues, preferring primary venue first for ordering.
 *
 * @param {Array<{ venueId: string, name?: string }>} linkedVenues
 * @param {{ primaryVenueId?: string|null }} [options]
 * @returns {Promise<{ users: object[], errors: string[] }>}
 */
export async function loadCatchBoardVenueUsersForDeal(
  linkedVenues = [],
  { primaryVenueId = null } = {},
) {
  const venues = Array.isArray(linkedVenues) ? linkedVenues : []
  if (!venues.length) {
    return { users: [], errors: [] }
  }

  const primary = primaryVenueId ? String(primaryVenueId) : null
  const ordered = [...venues].sort((left, right) => {
    const leftPrimary = String(left.venueId) === primary ? 0 : 1
    const rightPrimary = String(right.venueId) === primary ? 0 : 1
    return leftPrimary - rightPrimary
  })

  const results = await Promise.all(
    ordered.map(async venue => {
      try {
        const response = await fetchSpartacusVenueUsers(venue.venueId)
        const mapped = mapSpartacusVenueUsersResponse(response)
        return {
          venueId: String(venue.venueId),
          venueName: venue.name || mapped.venueName || '',
          users: mapped.users || [],
          error: null,
        }
      } catch (error) {
        return {
          venueId: String(venue.venueId),
          venueName: venue.name || '',
          users: [],
          error: error.message || 'Failed to load venue users',
        }
      }
    }),
  )

  const seen = new Set()
  const users = []
  const errors = []

  results.forEach(result => {
    if (result.error) {
      errors.push(
        result.venueName
          ? `${result.venueName}: ${result.error}`
          : result.error,
      )
    }

    ;(result.users || []).forEach(user => {
      const buyer = spartacusUserToCatchBoardBuyer(user)
      if (!buyer.accountId && !buyer.email && !buyer.name) {
        return
      }
      const key = buyer.accountId
        ? `account:${buyer.accountId}`
        : `email:${buyer.email.toLowerCase()}`
      if (seen.has(key)) {
        return
      }
      seen.add(key)
      users.push({
        ...buyer,
        venueId: result.venueId,
        venueName: result.venueName,
        isOwner: Boolean(user.isOwner),
      })
    })
  })

  return { users, errors }
}
